import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {STACKOVERFLOW_MESSAGE} from 'src/constants';
import {useBackgroundTaskManager} from 'src/contexts/background-task-context';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {getLicenseCaption} from 'src/modules/profile/account/tabs/license-and-features/basic-tab-scructure/components/license-suggest/utils';
import {selectAccountLicenseHistory} from 'src/modules/profile/account/tabs/license-and-features/services/account-license-features-slice';
import {importSyncedEntitiesData} from 'src/openapi/admin/apis/ImportSyncedEntitiesApi';
import {useAppDispatch} from 'src/store/hooks';

import Button from '@pb/kit-component-button';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Integer} from '@pb/kit-component-formatters';
import {Group} from '@pb/kit-component-group';
import Loading from '@pb/kit-component-loading';
import Table, {Body, Cell, Head, Row} from '@pb/kit-component-table';
import {LocaleContext} from '@pb/kit-context-locale';

import {
    IMPORT_TASK_FAILURE_LABEL,
    IMPORT_TASK_NAME,
    IMPORT_TASK_PROGRESS_LABEL,
    IMPORT_TASK_SUCCESS_LABEL,
    MODAL_CAPTION,
    MODAL_DESCRIPTION,
    NOT_ENOUGH_CREDITS_MODAL_DESCRIPTION,
} from './constants';
import {useImportSyncedEntities} from './useImportSyncedEntities';

import type {CrmType} from 'src/openapi/admin/models/CrmType';

type Props = {
    accountId: number,
    targetLicenseId: number,
    crmType: CrmType,
    onClose: () => void,
};

export const ImportSyncedEntitiesModal = ({onClose, targetLicenseId, accountId, crmType}: Props) => {
    const {locale} = useContext(LocaleContext);
    const apiDispatch = useAppDispatch();
    const {isFutureDate} = useDateTime();
    const createBackgroundTask = useBackgroundTaskManager();
    const licenseHistory = useSelector(selectAccountLicenseHistory);

    const {
        isLoading,
        loadingError,
        data: syncedEntitiesLicenses,
    } = useImportSyncedEntities({accountId, crmType, targetLicenseId});
    const [sourceLicenseId, setSourceLicenseId] = useState<number | undefined>();

    useEffect(() => {
        if (syncedEntitiesLicenses.length) {
            setSourceLicenseId(syncedEntitiesLicenses[0].licenseId);
        }
    }, [syncedEntitiesLicenses]);

    const selectedEntities = useMemo(() => {
        return syncedEntitiesLicenses.find(({licenseId}) => licenseId === sourceLicenseId);
    }, [sourceLicenseId, syncedEntitiesLicenses]);

    const importSyncedEntities = useCallback(async () => {
        onClose();

        const task = createBackgroundTask(
            IMPORT_TASK_NAME,
            <span>{IMPORT_TASK_PROGRESS_LABEL}</span>,
            {
                maxStack: 1,
                stackOverflowMessage: STACKOVERFLOW_MESSAGE,
            },
        );

        if (task && sourceLicenseId) {
            const importResult = await apiDispatch(importSyncedEntitiesData({
                accountId,
                crmType,
                sourceLicenseId,
                targetLicenseId,
            }));
            if (importResult.payload.success) {
                task.completeSuccess(<b>{IMPORT_TASK_SUCCESS_LABEL}</b>);
            } else {
                task.completeFailure(<b>{IMPORT_TASK_FAILURE_LABEL}</b>);
            }
        }
    }, [accountId, crmType, sourceLicenseId, targetLicenseId, apiDispatch, createBackgroundTask, onClose]);

    const notEnoughCredits = useMemo(
        () => selectedEntities && (selectedEntities.creditsRequired > selectedEntities.creditsRemaining),
        [selectedEntities],
    );

    const activeLicense = useMemo(
        () => licenseHistory?.find(({isActive}) => isActive),
        [licenseHistory],
    );

    const futureLicenses = useMemo(
        () => licenseHistory?.filter(({startDate}) => isFutureDate(startDate)),
        [licenseHistory, isFutureDate],
    );

    const expiredLicenses = useMemo(
        () => licenseHistory?.filter(({startDate, isActive}) => !isFutureDate(startDate) && !isActive),
        [isFutureDate, licenseHistory],
    );

    const caption = useMemo(() => {
        const futureTargetLicense = futureLicenses?.find(({licenseId}) => licenseId === targetLicenseId);
        if (futureTargetLicense) {
            return `${MODAL_CAPTION} ${getLicenseCaption(futureTargetLicense, locale.dateFormat, 'Pending')}`;
        }

        if (activeLicense?.licenseId === targetLicenseId) {
            return `${MODAL_CAPTION} ${getLicenseCaption(activeLicense, locale.dateFormat, 'Active')}`;
        }

        return MODAL_CAPTION;
    }, [targetLicenseId, activeLicense, futureLicenses, locale.dateFormat]);

    return (
        <>
            <DialogHeader onClose={onClose}>
                {caption}
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                <Skeleton shown={isLoading} view={<Loading fast={true}/>}>
                    <ServerResponseHandler serverError={loadingError} smallView={true}>
                        <Group direction="v" gap="l">
                            {selectedEntities && (selectedEntities.creditsRequired > selectedEntities.creditsRemaining)
                                ? NOT_ENOUGH_CREDITS_MODAL_DESCRIPTION
                                : MODAL_DESCRIPTION}
                            <ComboBox
                                value={sourceLicenseId}
                                disabled={!syncedEntitiesLicenses.length}
                                placeholder={syncedEntitiesLicenses.length
                                    ? 'Select Source License'
                                    : 'No Source Licenses Available'}
                                onChange={setSourceLicenseId}>
                                {[...futureLicenses?.length ? futureLicenses.filter(futureLicense =>
                                    syncedEntitiesLicenses
                                        .map(({licenseId}) => licenseId)
                                        .includes(futureLicense.licenseId),
                                ).map(license => (
                                    <ComboboxItem
                                        meta={syncedEntitiesLicenses
                                            .find(({licenseId}) => licenseId === license.licenseId)?.notes}
                                        caption={getLicenseCaption(license, locale.dateFormat, 'Pending')}
                                        value={license.licenseId}
                                        key={license.licenseId}
                                    />
                                )) : [],
                                ...(activeLicense && syncedEntitiesLicenses
                                    .map(({licenseId}) => licenseId)
                                    .includes(activeLicense.licenseId)) ? [
                                            <ComboboxItem
                                                meta={syncedEntitiesLicenses
                                                    .find(
                                                        ({licenseId}) => licenseId === activeLicense.licenseId,
                                                    )?.notes}
                                                caption={getLicenseCaption(activeLicense, locale.dateFormat, 'Active')}
                                                value={activeLicense.licenseId}
                                                key={activeLicense.licenseId}
                                            />,
                                        ] : [],
                                ...expiredLicenses?.length ? expiredLicenses.filter(expiredLicense =>
                                    syncedEntitiesLicenses
                                        .map(({licenseId}) => licenseId)
                                        .includes(expiredLicense.licenseId),
                                ).map(license => (
                                    <ComboboxItem
                                        meta={syncedEntitiesLicenses
                                            .find(({licenseId}) => licenseId === license.licenseId)?.notes}
                                        caption={getLicenseCaption(license, locale.dateFormat, 'Expired')}
                                        value={license.licenseId}
                                        key={license.licenseId}
                                    />
                                )) : []]}
                            </ComboBox>

                            {selectedEntities ? (
                                <Table borderedGrid={true}>
                                    <Head>
                                        <Cell>Credits Required</Cell>
                                        <Cell className={notEnoughCredits ? 'negative' : undefined}>
                                            Credits Remaining
                                        </Cell>
                                    </Head>
                                    <Body>
                                        <Row showZebra={false}>
                                            <Cell>
                                                {Integer.format(
                                                    selectedEntities?.creditsRequired,
                                                    locale,
                                                )}
                                            </Cell>
                                            <Cell className={notEnoughCredits ? 'negative' : undefined}>
                                                {Integer.format(
                                                    selectedEntities.creditsRemaining,
                                                    locale,
                                                )}
                                            </Cell>
                                        </Row>
                                    </Body>
                                </Table>
                            ) : null}
                            {!notEnoughCredits && selectedEntities ? (
                                <Table borderedGrid={true}>
                                    <Head>
                                        <Cell>Entity Type</Cell>
                                        <Cell>Synced Entities</Cell>
                                    </Head>
                                    <Body>
                                        {selectedEntities.syncedEntitiesCounts.map(({crmEntityTypeInfo, count}) => (
                                            <Row key={crmEntityTypeInfo.crmEntityType}>
                                                <Cell>{crmEntityTypeInfo.title}</Cell>
                                                <Cell>{Integer.format(count, locale)}</Cell>
                                            </Row>
                                        ))}
                                    </Body>
                                </Table>
                            ) : null}
                        </Group>
                    </ServerResponseHandler>
                </Skeleton>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button
                        variant="Type.PRIMARY"
                        disabled={notEnoughCredits || !selectedEntities?.importAllowed}
                        onClick={importSyncedEntities}>
                        Import
                    </Button>
                    <Button variant="Type.SECONDARY" onClick={onClose}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </>
    );
};
